import { PkButton, PkCheckbox, PkIcon, PkImage } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { useGetTeamsFromCompetitionQuery } from 'api/fspApi';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

export interface TeamModalInterface {
  isOpen: boolean;
  setClose: () => void;
  openNearlyDoneModal: (sortedTeams: Array<string>) => void;
  competitionId: string;
  competitionName: string;
  competitionShortName: string;
  seasonYear: string;
  competitionLogoName: string;
}

export const TeamModal = ({
  isOpen,
  setClose,
  openNearlyDoneModal,
  competitionId,
  competitionShortName,
  competitionName,
  seasonYear,
  competitionLogoName,
}: TeamModalInterface) => {
  const { data: teams = [], isLoading: teamsLoading } = useGetTeamsFromCompetitionQuery({
    id: competitionId,
    seasonYear,
  });
  const { t } = useTranslation();

  if (teamsLoading) {
    return <div />;
  }

  const sortedTeams = [...teams].sort((team, nextTeam) =>
    team?.internationalName?.localeCompare(nextTeam?.internationalName),
  );

  const competitionLogo = `${import.meta.env.BASE_URL}assets/competitionLogos/${competitionLogoName}`;
  const selectedTeams: Array<string> = [];

  const addTeam = (event: CustomEvent, id: string) => {
    if (event.detail.isChecked) {
      selectedTeams.push(id);
    } else {
      for (let i = 0; i < selectedTeams.length; i++) {
        if (selectedTeams[i] === id) {
          selectedTeams.splice(i, 1);
        }
      }
    }
  };

  const canOpenNearlyDone = () => {
    if (selectedTeams.length > 0) {
      openNearlyDoneModal(selectedTeams);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      appElement={document.getElementById('app') as HTMLElement}
      onRequestClose={setClose}
      closeTimeoutMS={250}
      className={{
        base: style.reactModalOverlay,
        afterOpen: style.afterOpen,
        beforeClose: style.beforeClose,
      }}
      style={{
        overlay: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgb(0 0 0 / 50%)',
          zIndex: 9999,
        },
        content: {
          position: 'relative',
          top: '40px',
          inset: '0px',
          bottom: '40px',
          boxShadow: '0 3px 9px rgb(0 0 0 / 50%)',
          background: '#fff',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderRadius: '4px',
          outline: 'none',
          maxWidth: '1392px',
          maxHeight: '600px',
          margin: 'auto',
          marginTop: '40px',
          padding: '0px',
        },
      }}
      bodyOpenClassName={style.reactModalOpen}
      contentLabel={t('add_to_calendar')}
    >
      <div className="pk-container pk-p--0 pk-gap--0" pk-competition={competitionShortName} pk-theme="light">
        <div className="pk-col pk-col--span-12 pk-d--flex pk-align-items--center pk-justify-content--between pk-bg--secondary-01">
          <PkImage src={competitionLogo} className="pk-ml--s pk-my--xs" height={38} alt={competitionName} />
          <PkButton appearance="text" onPkClick={setClose} className="pk-pr--s">
            <PkIcon name="close-web-icon" class="pk-text--text-04" size={15} />
          </PkButton>
        </div>
        <div
          className="pk-col pk-col--span-12 pk-px--m pk-pt--m"
          style={{ backgroundColor: '#eee', maxHeight: '450px', height: 'calc(100vh - 170px)' }}
        >
          <div
            className={clsx(style.teamsContainer, 'pk-overflow--scroll')}
            style={{ maxHeight: '430px', height: 'calc(100vh - 190px)', backgroundColor: 'rgba(255,255,255,.5)' }}
          >
            {sortedTeams.map((team, key) => {
              return (
                <div key={key} className="pk-pb--s pk-pt--s">
                  <PkCheckbox
                    id={team.id}
                    uid={team.id}
                    value={team.id}
                    name="checkbox-storybook"
                    disabled={false}
                    checked={false}
                    indeterminate={false}
                    onCheckboxChange={(e: CustomEvent) => addTeam(e, team.id)}
                  >
                    <img src={team.logoUrl} style={{ height: 32 }} className="pk-h" />
                    <span
                      className="pk-pl--xs pk-d--inline-block pk-align--middle pk-text--text-01"
                      style={{ maxWidth: 110 }}
                    >
                      {translateEntity(team)?.displayOfficialName}
                    </span>
                  </PkCheckbox>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="pk-col pk-col--span-12 pk-m--0 pk-p--0 pk-bg--ui-02 pk-bottom--0"
          style={{ boxShadow: '0 0 8px 4px rgb(0 0 0 / 18%)' }}
        >
          <div className="pk-m--0 pk-p--0">
            <div className="pk-float--right">
              <PkButton className="pk-pt--s pk-pb--s pk-pr--s" width="200" onPkClick={canOpenNearlyDone}>
                <PkIcon name="actions-add-to-calendar" size={20} class="pk-mr--xs" /> {t('add_to_calendar')}
              </PkButton>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
